/**
 * Three-way merge of an owner draft against the newer server trip.
 * Inputs: base (the revision the draft started from), server, draft.
 * Only fields the owner actually changed from base are applied. Concurrent
 * additions keep both. Same-field conflicts keep the server value and are listed.
 */

import type { TripDocument } from "./schema.ts";

export type MergeConflict = {
  entity: string;
  id: string;
  field: string;
};

export type MergeResult = {
  doc: TripDocument;
  conflicts: MergeConflict[];
};

function byId<T extends { id: string }>(rows: T[]): Map<string, T> {
  return new Map(rows.map((row) => [row.id, row]));
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function mergeRecord<T extends { id: string }>(
  entity: string,
  base: T | undefined,
  server: T | undefined,
  draft: T | undefined,
  conflicts: MergeConflict[],
): T | undefined {
  if (!draft && !server) return undefined;
  if (!draft) return server;
  if (!server) return draft;
  if (!base) {
    return same(server, draft) ? server : server;
  }
  const result = { ...server } as T;
  for (const key of Object.keys(draft) as Array<keyof T & string>) {
    if (key === "id") continue;
    const draftVal = draft[key];
    const baseVal = base[key];
    const serverVal = server[key];
    if (same(draftVal, baseVal)) continue;
    if (same(serverVal, baseVal) || same(serverVal, draftVal)) {
      result[key] = draftVal;
      continue;
    }
    conflicts.push({ entity, id: draft.id, field: key });
  }
  return result;
}

function threeWayById<T extends { id: string }>(
  entity: string,
  base: T[],
  server: T[],
  draft: T[],
  conflicts: MergeConflict[],
): T[] {
  const baseMap = byId(base);
  const serverMap = byId(server);
  const draftMap = byId(draft);
  const ids = new Set([...baseMap.keys(), ...serverMap.keys(), ...draftMap.keys()]);
  const rows: T[] = [];
  const seen = new Set<string>();

  function take(id: string) {
    if (seen.has(id)) return;
    const b = baseMap.get(id);
    const s = serverMap.get(id);
    const d = draftMap.get(id);
    if (b && !d) {
      if (s && !same(s, b)) {
        conflicts.push({ entity, id, field: "*" });
        rows.push(s);
        seen.add(id);
      }
      return;
    }
    if (b && !s) {
      if (d && !same(d, b)) {
        conflicts.push({ entity, id, field: "*" });
        rows.push(d);
        seen.add(id);
      }
      return;
    }
    const merged = mergeRecord(entity, b, s, d, conflicts);
    if (merged) {
      rows.push(merged);
      seen.add(id);
    }
  }

  for (const row of server) take(row.id);
  for (const row of draft) take(row.id);
  for (const id of ids) take(id);
  return rows;
}

function mergeSettings(
  base: TripDocument["trip"],
  server: TripDocument["trip"],
  draft: TripDocument["trip"],
  conflicts: MergeConflict[],
): TripDocument["trip"] {
  const result = { ...server };
  for (const key of Object.keys(draft) as Array<keyof TripDocument["trip"] & string>) {
    if (same(draft[key], base[key])) continue;
    if (same(server[key], base[key]) || same(server[key], draft[key])) {
      (result as Record<string, unknown>)[key] = draft[key];
      continue;
    }
    conflicts.push({ entity: "trip", id: server.id, field: key });
  }
  return result;
}

export function mergeOwnerDraft(
  server: TripDocument,
  draft: TripDocument,
  base?: TripDocument | null,
): TripDocument {
  return mergeOwnerDraftDetailed(server, draft, base).doc;
}

export function mergeOwnerDraftDetailed(
  server: TripDocument,
  draft: TripDocument,
  base?: TripDocument | null,
): MergeResult {
  const conflicts: MergeConflict[] = [];
  if (!base) {
    return {
      doc: {
        ...server,
        places: threeWayById("places", server.places, server.places, draft.places, conflicts),
        stays: threeWayById("stays", server.stays, server.stays, draft.stays, conflicts),
        bookings: threeWayById("bookings", server.bookings, server.bookings, draft.bookings, conflicts),
        flights: threeWayById("flights", server.flights, server.flights, draft.flights, conflicts),
        events: threeWayById("events", server.events, server.events, draft.events, conflicts),
        contacts: threeWayById("contacts", server.contacts, server.contacts, draft.contacts, conflicts),
        supplierPayments: threeWayById("supplierPayments", server.supplierPayments, server.supplierPayments, draft.supplierPayments, conflicts),
        supplierRefunds: threeWayById("supplierRefunds", server.supplierRefunds, server.supplierRefunds, draft.supplierRefunds, conflicts),
        householdSettlements: threeWayById("householdSettlements", server.householdSettlements, server.householdSettlements, draft.householdSettlements, conflicts),
        attachments: threeWayById("attachments", server.attachments, server.attachments, draft.attachments, conflicts),
        reviewItems: threeWayById("reviewItems", server.reviewItems, server.reviewItems, draft.reviewItems, conflicts),
        ideas: threeWayById("ideas", server.ideas, server.ideas, draft.ideas, conflicts),
        preparationItems: threeWayById("preparationItems", server.preparationItems, server.preparationItems, draft.preparationItems, conflicts),
        thaiPhrases: threeWayById("thaiPhrases", server.thaiPhrases, server.thaiPhrases, draft.thaiPhrases, conflicts),
        memories: threeWayById("memories", server.memories, server.memories, draft.memories, conflicts),
      },
      conflicts,
    };
  }

  return {
    doc: {
      ...server,
      trip: mergeSettings(base.trip, server.trip, draft.trip, conflicts),
      places: threeWayById("places", base.places, server.places, draft.places, conflicts),
      stays: threeWayById("stays", base.stays, server.stays, draft.stays, conflicts),
      bookings: threeWayById("bookings", base.bookings, server.bookings, draft.bookings, conflicts),
      flights: threeWayById("flights", base.flights, server.flights, draft.flights, conflicts),
      events: threeWayById("events", base.events, server.events, draft.events, conflicts),
      contacts: threeWayById("contacts", base.contacts, server.contacts, draft.contacts, conflicts),
      supplierPayments: threeWayById("supplierPayments", base.supplierPayments, server.supplierPayments, draft.supplierPayments, conflicts),
      supplierRefunds: threeWayById("supplierRefunds", base.supplierRefunds, server.supplierRefunds, draft.supplierRefunds, conflicts),
      householdSettlements: threeWayById("householdSettlements", base.householdSettlements, server.householdSettlements, draft.householdSettlements, conflicts),
      attachments: threeWayById("attachments", base.attachments, server.attachments, draft.attachments, conflicts),
      reviewItems: threeWayById("reviewItems", base.reviewItems, server.reviewItems, draft.reviewItems, conflicts),
      ideas: threeWayById("ideas", base.ideas, server.ideas, draft.ideas, conflicts),
      preparationItems: threeWayById("preparationItems", base.preparationItems, server.preparationItems, draft.preparationItems, conflicts),
      thaiPhrases: threeWayById("thaiPhrases", base.thaiPhrases, server.thaiPhrases, draft.thaiPhrases, conflicts),
      memories: threeWayById("memories", base.memories, server.memories, draft.memories, conflicts),
    },
    conflicts,
  };
}
